import * as React from 'react';
import { FormControl, InputGroup } from 'react-bootstrap';

import Tooltip from 'af-components/Tooltip';

import type { CellError } from '../../error.types';
import type { GlyphItem } from '../Glyph';
import Glyph from '../Glyph';
import styles from '../styles.module.scss';
import type { InputCellType, InputSharedProps } from '../types';

type FieldValue = string | number | undefined;

/**
 * RHFFieldProps are props coming from field in react-hook-form
 */
interface RHFFieldProps {
	name: string;
	value: FieldValue;
	onChange: React.ChangeEventHandler;
	onBlur: (event) => void;
}

interface Props extends RHFFieldProps, InputSharedProps {
	elementKey: string;
	/**
	 * For now type of input cell can be: text, numeric, dollar or quantity cell
	 * Depending on type, design is decided
	*
	* @default text
	*/
	type?: InputCellType;
	placeholder?: string;
	className?: string;
	error?: CellError;
}

const isNumericInputType = (type: InputCellType) => {
	return type === 'numeric'
		|| type === 'dollar'
		|| type === 'quantity'
		|| type === 'percent';
};

const sanitizeNumericInputValue = (value: string) => {
	const normalizedValue = value.replace(',', '.');

	const numericCharactersOnly = normalizedValue.replace(/[^\d.]/g, '');

	const firstDecimalPointIndex = numericCharactersOnly.indexOf('.');

	if (firstDecimalPointIndex === -1) {
		return numericCharactersOnly;
	}

	const beforeDecimalPoint = numericCharactersOnly.slice(0, firstDecimalPointIndex + 1);
	const afterDecimalPoint = numericCharactersOnly
		.slice(firstDecimalPointIndex + 1)
		.replace(/\./g, '');

	return `${beforeDecimalPoint}${afterDecimalPoint}`;
};

export const InputCell: React.FC<Props> = React.memo((props) => {

	const {
		placeholder,
		type = 'text',
		name,
		elementKey,
		value,
		onChange,
		onBlur,
		className,
		error,
		prefixGlyphs = [],
		suffixGlyphs = [],
	} = props;

	const inputRef = React.useRef<HTMLInputElement | null>(null);
	const hasAutoFocused = React.useRef(false);

	const [focused, setFocused] = React.useState(false);

	const id = React.useMemo(() => (
		`accqa__input__${elementKey}`
	), [elementKey]);

	const resolvedClassName = React.useMemo(() => {
		let cn = className ?? `${styles.input}`;

		if (type === 'dollar') {
			cn = `${cn} ${styles['dollar-value']}`;
		} else if (type === 'numeric') {
			cn = `${cn} ${styles['numeric-value']}`;
		} else if (type === 'quantity') {
			cn = `${cn} ${styles['money-or-qty-value']}`;
		} else if (type === 'percent') {
			cn = `${cn} ${styles['percent-value']}`;
		}
		if (error) {
			cn = `${cn} ${styles['with-error']}`;
			if (error.type === 'tooltip') {
				cn = `${cn} ${styles['with-tooltip']}`;
			} else if (error.type === 'required') {
				cn = `${cn} ${styles['error-placeholder']}`;
			}
		}
		return cn;
	}, [className, error, type]);

	const resolvedPrefixGlyphs = React.useMemo<GlyphItem[]>(() => {
		const glyphs = [...prefixGlyphs];
		if (type === 'dollar') {
			glyphs.unshift({ type: 'icon', value: 'dollar' });
		}
		return glyphs;
	}, [prefixGlyphs, type]);

	const resolvedSuffixGlyphs = React.useMemo<GlyphItem[]>(() => {
		const glyphs = [...suffixGlyphs];
		if (type === 'percent') {
			glyphs.unshift({ type: 'text', value: '%' });
		}
		return glyphs;
	}, [suffixGlyphs, type]);

	const renderGlyph = React.useCallback((glyph: GlyphItem) => {
		return <Glyph glyph={glyph} />;
	}, []);

	React.useEffect(() => {
		if (!hasAutoFocused.current) {
			inputRef.current?.focus();
			hasAutoFocused.current = true;
		}
	}, []);

	const handleFocus = React.useCallback(() => {
		setFocused(true);
	}, []);

	const handleBlur = React.useCallback((event) => {
		hasAutoFocused.current = false; // Reset for next time
		setFocused(false);
		onBlur(event);
	}, [onBlur]);

	const handleChange = React.useCallback((event) => {
		if (isNumericInputType(type)) {
			event.target.value = sanitizeNumericInputValue(event.target.value);
		}

		if (onChange) {
			onChange(event);
		}
	}, [onChange, type]);

	const renderCell = React.useMemo(() => {
		const placeholderInCell = error?.type === 'required'
			? error?.message
			: placeholder
				? placeholder
				: '-';

		return (
			<InputGroup className={`${styles['input-wrapper']}`}>
				{!!resolvedPrefixGlyphs.length && (
					<div className={styles.glyphs}>
						{resolvedPrefixGlyphs.map(renderGlyph)}
					</div>
				)}
				<FormControl
					className={resolvedClassName}
					id={id}
					key={elementKey}
					name={name}
					onBlurCapture={handleBlur}
					onChange={handleChange}
					onFocus={handleFocus}
					placeholder={!focused ? placeholderInCell : undefined}
					ref={inputRef}
					spellCheck="false"
					value={value ?? ''}
				/>
				{!!resolvedSuffixGlyphs.length && (
					<div className={styles.glyphs}>
						{resolvedSuffixGlyphs.map(renderGlyph)}
					</div>
				)}
				{error?.type === 'tooltip' && error.message &&
					<Tooltip message={error.message} placement="auto">
						<span className="icon-notification_status" />
					</Tooltip>
				}
			</InputGroup>
		);
	}, [
		error?.type,
		error?.message,
		placeholder,
		resolvedPrefixGlyphs,
		renderGlyph,
		resolvedClassName,
		id,
		elementKey,
		name,
		handleBlur,
		handleChange,
		handleFocus,
		focused,
		value,
		resolvedSuffixGlyphs,
	]);

	return renderCell;
});
